/**
 * Service Worker Registration
 * Registers the service worker for background caching
 */

'use client';

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('[ServiceWorker] Registered successfully:', registration);
    }

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker available
            if (process.env.NODE_ENV === 'development') {
              console.log('[ServiceWorker] New version available - refreshing page');
            }
            // Force page refresh to activate new service worker
            window.location.reload();
          }
        });
      }
    });

    return registration;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ServiceWorker] Registration failed:', error);
    }
    return null;
  }
}

/**
 * Unregister service worker (for development/testing)
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const unregistered = await registration.unregister();
    if (process.env.NODE_ENV === 'development') {
      console.log('[ServiceWorker] Unregistered:', unregistered);
    }
    return unregistered;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ServiceWorker] Unregistration failed:', error);
    }
    return false;
  }
}

/**
 * Invalidate a URL in the service worker cache
 */
export async function invalidateServiceWorkerCache(url: string): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage({
        type: 'INVALIDATE_CACHE',
        url,
      });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ServiceWorker] Failed to invalidate cache:', error);
    }
  }
}

/**
 * Clear all service worker caches
 */
export async function clearServiceWorkerCache(): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage({
        type: 'CLEAR_CACHE',
      });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ServiceWorker] Failed to clear cache:', error);
    }
  }
}
